"use client";

import React from "react";
import { FileText, Mail, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const legalSections = [{
  number: "01",
  title: "Identidad del responsable",
  content: "El presente sitio web es operado bajo la marca comercial Spora (en lo sucesivo, 'Spora'), con domicilio en la República Mexicana. Spora se dedica a la formulación, distribución y comercialización de productos cosméticos, de cuidado personal y bienestar basados en extractos de hongos funcionales."
}, {
  number: "02",
  title: "Aceptación de los términos",
  content: "Al acceder, navegar o realizar compras en este sitio web, usted acepta quedar vinculado por estos Términos y Condiciones, así como por nuestra Política de Privacidad. Si no está de acuerdo con la totalidad de estos términos, le solicitamos abstenerse de utilizar la plataforma y adquirir productos."
}, {
  number: "03",
  title: "Descripción y uso de los productos",
  content: "Spora comercializa cremas faciales y corporales, infusiones botánicas, tinturas y kits integrales de cuidado. Nuestros productos cosméticos y de bienestar son para uso personal y no pretenden diagnosticar, tratar, curar ni prevenir ninguna enfermedad. Recomendamos revisar cuidadosamente la lista de ingredientes para evitar posibles reacciones alérgicas antes de iniciar cualquier rutina."
}, {
  number: "04",
  title: "Precios y moneda",
  content: "Todos los precios exhibidos en la tienda en línea se expresan en Pesos Mexicanos (MXN) e incluyen el Impuesto al Valor Agregado (IVA) aplicable, salvo que se indique expresamente lo contrario. Los costos de envío se desglosarán de forma transparente durante el proceso de pago antes de confirmar la orden."
}, {
  number: "05",
  title: "Proceso de compra y pagos",
  content: "Para adquirir productos, el usuario deberá seleccionar los artículos, agregarlos al carrito de compra e ingresar la información de envío y facturación requerida. Los pagos se procesan a través de pasarelas seguras y encriptadas. Spora se reserva el derecho de cancelar o rechazar órdenes en caso de sospecha de fraude o inconsistencias en los datos proporcionados."
}, {
  number: "06",
  title: "Envíos y entregas en México",
  content: "Realizamos envíos a toda la República Mexicana mediante empresas de mensajería certificadas. Los plazos estimados de entrega comienzan a contar a partir de la confirmación del pago. Spora no será responsable por retrasos derivados de causas de fuerza mayor o eventos fuera de nuestro control razonable."
}, {
  number: "07",
  title: "Propiedad intelectual",
  content: "Todo el contenido disponible en este sitio, incluyendo marcas, logotipos, textos, gráficos, fotografías, diseños de empaque y material educativo sobre hongos funcionales, es propiedad exclusiva de Spora o se encuentra debidamente licenciado. Queda estrictamente prohibida su reproducción, copia o distribución sin autorización previa por escrito."
}, {
  number: "08",
  title: "Obligaciones y conducta del usuario",
  content: "El usuario se compromete a proporcionar información verídica, actual y completa al momento de registrarse o comprar, así como a no utilizar el sitio con fines ilícitos, lesivos de derechos de terceros o que puedan dañar o sobrecargar los servidores de la plataforma."
}, {
  number: "09",
  title: "Limitación de responsabilidad",
  content: "En la medida máxima permitida por la legislación aplicable en México, Spora no será responsable por daños indirectos, incidentales o consecuentes derivados del uso indebido del sitio web o de la incorrecta aplicación de los productos fuera de las instrucciones recomendadas."
}, {
  number: "10",
  title: "Legislación aplicable y jurisdicción",
  content: "Los presentes Términos y Condiciones se rigen e interpretan de conformidad con las leyes federales de los Estados Unidos Mexicanos y la Ley Federal de Protección al Consumidor (PROFECO). Para cualquier controversia, las partes se someten a la jurisdicción de los tribunales competentes de la Ciudad de México, renunciando a cualquier otro fuero que pudiera corresponderles."
}];
export default function Terms() {
  return <section id="termsandconditions" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Header Block */}
        <div className="text-center md:text-left pb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-medium mb-4">
            <ShieldCheck className="size-3.5" />
            <span>Marco legal y transparencia</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight text-foreground">
            Términos y Condiciones
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground font-sans">
            Última actualización: 24 de mayo de 2024
          </p>
        </div>

        <Separator className="my-6" />

        {/* Introduction */}
        <div className="prose prose-neutral my-8 text-foreground/90 font-sans leading-relaxed text-sm sm:text-base">
          <p>
            Bienvenida y bienvenido a Spora. Los siguientes términos regulan el
            acceso y uso de nuestra tienda digital, así como la compra de
            nuestros productos de belleza y bienestar elaborados con extractos de
            hongos funcionales en el territorio mexicano.
          </p>
        </div>

        {/* Legal Sections List */}
        <div className="space-y-10 font-sans">
          {legalSections.map((section, i) => <article key={section.number} data-index={i} className="group border-b border-border pb-8 last:border-b-0">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-mono text-xs font-semibold text-primary/80">
                  {section.number}
                </span>
                <h2 className="font-serif text-xl sm:text-2xl font-normal text-foreground">
                  {section.title}
                </h2>
              </div>
              <p className="mt-3 text-sm sm:text-base text-foreground/80 leading-relaxed pl-7">
                {section.content}
              </p>
            </article>)}
        </div>

        {/* Contact and Support Card */}
        <div className="mt-12 p-6 sm:p-8 rounded-xl bg-card border border-border text-card-foreground shadow-xs">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-accent text-accent-foreground shrink-0 mt-1">
              <Mail className="size-5" />
            </div>
            <div>
              <h3 className="font-serif text-lg sm:text-xl font-normal text-foreground">
                11. Contacto y atención al cliente
              </h3>
              <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                Si tiene dudas, comentarios o requiere aclaraciones sobre estos
                Términos y Condiciones o sobre alguna compra realizada, puede
                comunicarse directamente con nuestro equipo:
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <a href="mailto:contacto@spora.beauty" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                  <Mail className="size-4" />
                  contacto@spora.beauty
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Actions */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
          <Button variant="outline" asChild className="w-full sm:w-auto">
            <Link to="/ShopPage">Explorar productos</Link>
          </Button>
          <Button variant="ghost" asChild className="w-full sm:w-auto text-muted-foreground">
            <Link to="/HomePage">Volver al inicio</Link>
          </Button>
        </div>
      </div>
    </section>;
}